import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../api';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { createChart } from 'lightweight-charts';
import { getMaRibbonTradeLevels, isMaRibbonSignal } from '../../utils/niftyMaRibbonLevels';

const isEmaPullbackSignal = (signal) => {
  const st = (signal?.strategy_type || '').toLowerCase();
  return st === 'ema_pullback' || st === 'ema_pullback_continuation';
};

const getEmaPullbackTriggerBarLabel = (signal) => {
  if (!signal) return null;
  return (
    signal.ema_pullback_trigger_bar_time_ist
    || signal?.conditions_met?.ema_pullback_trigger_bar_time_ist
    || signal.ema_pullback_trigger_bar_time
    || signal?.conditions_met?.ema_pullback_trigger_bar_time
    || null
  );
};

const getEmaPullbackFastPathDetail = (signal) => {
  if (!signal) return null;
  return (
    signal.ema_pullback_fast_path_detail
    || signal?.conditions_met?.ema_pullback_fast_path_detail
    || null
  );
};

// Helper function to create a unique key for deduplication
const getSignalKey = (signal) => {
  // Create a unique key based on signal characteristics
  // This identifies duplicate signals that are the same but generated at different times
  const entry = signal.trade_details?.entry_price?.toFixed(2) || '0';
  const stopLoss = signal.trade_details?.stop_loss?.toFixed(2) || '0';
  const target = signal.trade_details?.target_1?.toFixed(2) || '0';
  const instrument = signal.trade_details?.instrument || '';
  const strategy = signal.strategy_type || signal.strategy_name || '';
  const signalType = signal.signal_type || '';
  
  // Combine all identifying characteristics
  return `${instrument}_${strategy}_${signalType}_${entry}_${stopLoss}_${target}`;
};

// Deduplicate signals - keep only the first occurrence (earliest timestamp)
const deduplicateSignals = (signalsList) => {
  const seen = new Map();
  const deduplicated = [];
  
  // Sort by timestamp (oldest first) to ensure we keep the first occurrence
  const sorted = [...signalsList].sort((a, b) => {
    const timeA = new Date(a.timestamp).getTime();
    const timeB = new Date(b.timestamp).getTime();
    return timeA - timeB;
  });
  
  for (const signal of sorted) {
    const key = getSignalKey(signal);
    
    // If we haven't seen this signal before, add it
    if (!seen.has(key)) {
      seen.set(key, true);
      deduplicated.push(signal);
    }
  }
  
  // Sort back by timestamp descending (newest first) for display
  return deduplicated.sort((a, b) => {
    const timeA = new Date(a.timestamp).getTime();
    const timeB = new Date(b.timestamp).getTime();
    return timeB - timeA;
  });
};

const NiftySignalHistoryPage = () => {
  const [searchParams] = useSearchParams();
  const isMobileEmbed = searchParams.get('tv_embed') === '1';

  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Filters
  const [strategyType, setStrategyType] = useState('');
  const [signalType, setSignalType] = useState('');
  const [outcome, setOutcome] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  const [expandedSignal, setExpandedSignal] = useState(null);

  const fetchSignals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });
      
      if (strategyType) params.append('strategy_type', strategyType);
      if (signalType) params.append('signal_type', signalType);
      if (outcome) params.append('outcome', outcome);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await api.get(`/api/v1/nifty/signal-history?${params.toString()}`);
      
      // Deduplicate signals before setting state
      const rawSignals = response.data.signals || [];
      const deduplicatedSignals = deduplicateSignals(rawSignals);
      
      setSignals(deduplicatedSignals);
      
      // Keep backend pagination counts to allow navigation across days
      setTotalCount(response.data.pagination?.total_count || 0);
      setTotalPages(response.data.pagination?.total_pages || 0);
    } catch (err) {
      console.error('Error fetching signal history:', err);
      setError(err.response?.data?.detail || 'Failed to fetch signal history');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, strategyType, signalType, outcome, startDate, endDate]);

  useEffect(() => {
    fetchSignals();
  }, [fetchSignals]);

  const handleResetFilters = () => {
    setStrategyType('');
    setSignalType('');
    setOutcome('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getTradeLevels = (signal) => {
    const td = signal?.trade_details || {};
    if (isMaRibbonSignal(signal)) {
      const levels = getMaRibbonTradeLevels({
        ...signal,
        entry_price: td.entry_price ?? signal?.entry_price,
        stop_loss: td.stop_loss ?? signal?.stop_loss,
        target_1: td.target_1 ?? signal?.target_1,
        target_2: td.target_2 ?? signal?.target_2,
        target_3: td.target_3 ?? signal?.target_3,
        index_entry: td.index_entry,
        index_stop_loss: td.index_stop_loss,
        index_target_1: td.index_target_1,
        index_target_2: td.index_target_2,
        index_target_3: td.index_target_3,
      });
      return {
        entry: levels.entry,
        target1: levels.target1,
        target2: levels.target2,
        target3: levels.target3,
        stopLoss: levels.stopLoss,
        indexEntry: levels.indexEntry,
        indexTarget1: levels.indexTarget1,
        indexTarget2: levels.indexTarget2,
        indexTarget3: levels.indexTarget3,
        indexStopLoss: levels.indexStopLoss,
      };
    }
    const entry = td.entry_price ?? null;
    const target1 = td.target_1 ?? (entry ? entry + 10 : null);
    const target2 = td.target_2 ?? (entry ? entry + 20 : null);
    const target3 = td.target_3 ?? (entry ? entry + 30 : null);
    const stopLoss = td.stop_loss ?? (entry ? entry - 10 : null);
    return {
      entry,
      target1,
      target2,
      target3,
      stopLoss,
      indexEntry: td.index_entry ?? null,
      indexTarget1: td.index_target_1 ?? null,
      indexTarget2: td.index_target_2 ?? null,
      indexTarget3: td.index_target_3 ?? null,
      indexStopLoss: td.index_stop_loss ?? null,
    };
  };

  /** Option price primary; NIFTY index on the next line for MA Ribbon signals */
  const formatPriceWithIndex = (optionPrice, indexPrice) => {
    if (optionPrice === null || optionPrice === undefined) return 'N/A';

    return (
      <div className="leading-tight">
        <div>₹{Number(optionPrice).toFixed(2)}</div>
        {indexPrice !== null && indexPrice !== undefined && (
          <div className="mt-0.5 text-xs text-gray-500">
            NIFTY: ₹{Number(indexPrice).toFixed(2)}
          </div>
        )}
      </div>
    );
  };

  const getOutcomeColor = (outcome) => {
    switch (outcome) {
      case 'Win':
        return 'text-green-600 bg-green-50';
      case 'Loss':
        return 'text-red-600 bg-red-50';
      case 'Breakeven':
        return 'text-yellow-600 bg-yellow-50';
      case 'Pending':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getSignalTypeColor = (type) => {
    return type === 'Bullish' 
      ? 'text-green-600 bg-green-100' 
      : 'text-red-600 bg-red-100';
  };

  const toggleExpand = (signalId) => {
    setExpandedSignal(expandedSignal === signalId ? null : signalId);
  };

  // Track pending signal outcomes
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingResult, setTrackingResult] = useState(null);

  const trackSignalOutcomes = async () => {
    try {
      setTrackingLoading(true);
      setTrackingResult(null);
      
      const response = await api.post('/api/v1/nifty/signals/track-outcomes');
      
      if (response.data.success) {
        setTrackingResult({
          type: 'success',
          message: `Tracking complete: ${response.data.results.wins} wins, ${response.data.results.losses} losses, ${response.data.results.still_pending} still pending`
        });
        // Refresh the signals list
        fetchSignals();
      }
    } catch (err) {
      console.error('Error tracking outcomes:', err);
      setTrackingResult({
        type: 'error',
        message: err.response?.data?.detail || 'Failed to track outcomes'
      });
    } finally {
      setTrackingLoading(false);
    }
  };

  // Track a single signal
  const trackSingleSignal = async (signalId) => {
    try {
      const response = await api.get(`/api/v1/nifty/signals/${signalId}/track`);
      
      if (response.data) {
        // Refresh to show updated data
        fetchSignals();
        
        if (response.data.status === 'resolved') {
          setTrackingResult({
            type: 'success',
            message: `Signal resolved: ${response.data.outcome} (${response.data.exit_reason})`
          });
        } else {
          const data = response.data;
          const points = data.points_from_entry;
          const pointsText = points !== null ? `${points >= 0 ? '+' : ''}${points.toFixed(2)} pts` : '';
          // Show Entry Price clearly so users understand Target/SL calculation
          setTrackingResult({
            type: 'info',
            message: `Entry: ₹${data.entry_price?.toFixed(2)} | LTP: ₹${data.current_ltp?.toFixed(2)} (${pointsText}) | Target: ₹${data.tracking_target?.toFixed(2)} (Entry+10) | SL: ₹${data.tracking_stoploss?.toFixed(2)} (Entry-10)`
          });
        }
      }
    } catch (err) {
      console.error('Error tracking signal:', err);
      setTrackingResult({
        type: 'error',
        message: err.response?.data?.detail || 'Failed to track signal'
      });
    }
  };

  // Live P&L State for checking current performance
  const [livePnL, setLivePnL] = useState({});
  const [livePnLLoading, setLivePnLLoading] = useState({});

  // Check Live P&L for a signal
  const checkLivePnL = async (signal) => {
    const instrument = signal.trade_details?.instrument;
    if (!instrument) {
      alert('No instrument found for this signal');
      return;
    }

    setLivePnLLoading(prev => ({ ...prev, [signal.signal_id]: true }));

    try {
      // Fetch current LTP from the option chart endpoint
      const response = await api.get(`/api/v1/nifty/option-chart/${instrument}?timeframe=5minute&days=1`);
      
      if (response.data.success && response.data.current_ltp) {
        const currentLTP = response.data.current_ltp;
        const { entry: entryPrice, target1, stopLoss } = getTradeLevels(signal);
        
        // Calculate P&L
        let pnlPercent = 0;
        let pnlAmount = 0;
        let status = 'Open';
        
        if (entryPrice && entryPrice > 0) {
          pnlAmount = currentLTP - entryPrice;
          pnlPercent = ((currentLTP - entryPrice) / entryPrice) * 100;
          
          // Check if target or SL would have been hit using stored strategy levels.
          if (target1 && currentLTP >= target1) {
            status = 'Target Hit';
          } else if (stopLoss && currentLTP <= stopLoss) {
            status = 'Stop Loss Hit';
          }
        }
        
        setLivePnL(prev => ({
          ...prev,
          [signal.signal_id]: {
            currentLTP,
            entryPrice,
            target1,
            stopLoss,
            pnlAmount,
            pnlPercent,
            status,
            timestamp: new Date().toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })
          }
        }));
      }
    } catch (err) {
      console.error('Error checking live P&L:', err);
      setLivePnL(prev => ({
        ...prev,
        [signal.signal_id]: {
          error: err.response?.data?.detail || 'Failed to fetch current price'
        }
      }));
    } finally {
      setLivePnLLoading(prev => ({ ...prev, [signal.signal_id]: false }));
    }
  };

  // Option Chart Popup State
  const [chartPopup, setChartPopup] = useState({
    isOpen: false,
    instrument: null,
    instrumentToken: null,
    loading: false,
    error: null,
    data: null
  });
  const chartContainerRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const candlestickSeriesRef = useRef(null);
  const optionWebSocketRef = useRef(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [currentLTP, setCurrentLTP] = useState(null);

  // Open option chart popup
  const openOptionChart = async (instrument) => {
    if (!instrument) return;
    
    setChartPopup({
      isOpen: true,
      instrument: instrument,
      instrumentToken: null,
      loading: true,
      error: null,
      data: null
    });

    try {
      const response = await api.get(`/api/v1/nifty/option-chart/${instrument}?timeframe=5minute&days=1`);
      
      if (response.data.success) {
        const instrumentToken = response.data.instrument_token;
        
        // Subscribe to option for live data
        if (instrumentToken) {
          try {
            await api.post(`/api/v1/nifty/subscribe-option?instrument_token=${instrumentToken}&instrument_symbol=${instrument}`);
            console.log(`✅ Subscribed to option ${instrument} (token: ${instrumentToken})`);
          } catch (subErr) {
            console.warn('Failed to subscribe to option:', subErr);
            // Continue anyway - can still show historical chart
          }
        }
        
        setChartPopup(prev => ({
          ...prev,
          loading: false,
          instrumentToken: instrumentToken,
          data: response.data
        }));
        setCurrentLTP(response.data.current_ltp);
      } else {
        throw new Error('Failed to fetch chart data');
      }
    } catch (err) {
      console.error('Error fetching option chart:', err);
      setChartPopup(prev => ({
        ...prev,
        loading: false,
        error: err.response?.data?.detail || 'Failed to load chart'
      }));
    }
  };

  // Close chart popup
  const closeChartPopup = () => {
    // Close WebSocket connection
    if (optionWebSocketRef.current) {
      optionWebSocketRef.current.close();
      optionWebSocketRef.current = null;
    }
    setWsConnected(false);
    
    // Unsubscribe from option
    if (chartPopup.instrumentToken) {
      api.post(`/api/v1/nifty/unsubscribe-option?instrument_token=${chartPopup.instrumentToken}`)
        .catch(err => console.warn('Failed to unsubscribe:', err));
    }
    
    // Remove chart
    if (chartInstanceRef.current) {
      chartInstanceRef.current.remove();
      chartInstanceRef.current = null;
    }
    
    candlestickSeriesRef.current = null;
    
    setChartPopup({
      isOpen: false,
      instrument: null,
      instrumentToken: null,
      loading: false,
      error: null,
      data: null
    });
    setCurrentLTP(null);
    setWsConnected(false);
  };

  // Render chart when data is loaded
  useEffect(() => {
    if (chartPopup.isOpen && chartPopup.data && chartContainerRef.current && !chartInstanceRef.current) {
      const chart = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: 400,
        layout: {
          background: { color: '#ffffff' },
          textColor: '#333',
        },
        grid: {
          vertLines: { color: '#e1e1e1' },
          horzLines: { color: '#e1e1e1' },
        },
        localization: {
          // Use IST timezone for crosshair tooltip
          timeFormatter: (timestamp) => {
            const date = new Date(timestamp * 1000);
            // Convert to IST using Intl.DateTimeFormat
            return new Intl.DateTimeFormat('en-IN', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
              timeZone: 'Asia/Kolkata'
            }).format(date);
          },
        },
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
          // X-axis tick labels formatter - shows IST time
          tickMarkFormatter: (time) => {
            const date = new Date(time * 1000);
            // Format time in IST
            return new Intl.DateTimeFormat('en-IN', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
              timeZone: 'Asia/Kolkata'
            }).format(date);
          },
        },
        rightPriceScale: {
          borderColor: '#cccccc',
        },
      });

      const candlestickSeries = chart.addCandlestickSeries({
        upColor: '#22c55e',
        downColor: '#ef4444',
        borderDownColor: '#ef4444',
        borderUpColor: '#22c55e',
        wickDownColor: '#ef4444',
        wickUpColor: '#22c55e',
      });

      candlestickSeries.setData(chartPopup.data.chart_data);
      candlestickSeriesRef.current = candlestickSeries;

      // Add current LTP line
      if (chartPopup.data.current_ltp > 0) {
        candlestickSeries.createPriceLine({
          price: chartPopup.data.current_ltp,
          color: '#2563eb',
          lineWidth: 2,
          lineStyle: 0,
          axisLabelVisible: true,
          title: 'LTP',
        });
      }

      chart.timeScale().fitContent();
      chartInstanceRef.current = chart;
      
      // Connect to WebSocket for live option candle updates
      if (chartPopup.instrumentToken) {
        // Get WebSocket URL from API base URL (same pattern as NIFTY50 chart)
        const apiUrl = api.defaults.baseURL || process.env.REACT_APP_API_URL || 'http://localhost:8000';
        let wsBaseUrl = apiUrl.replace(/^http/, 'ws');
        if (apiUrl.startsWith('https')) {
          wsBaseUrl = apiUrl.replace(/^https/, 'wss');
        }
        
        const token = localStorage.getItem('token') || localStorage.getItem('access_token');
        const wsUrl = `${wsBaseUrl}/api/v1/nifty/ws/option-data/${chartPopup.instrumentToken}?token=${token}`;
        
        console.log(`🔌 Connecting to option WebSocket: ${wsUrl.replace(/token=[^&]*/, 'token=***')}`);
        
        try {
          const ws = new WebSocket(wsUrl);
          optionWebSocketRef.current = ws;
          
          ws.onopen = () => {
            console.log(`✅ Connected to option WebSocket for token ${chartPopup.instrumentToken}`);
            setWsConnected(true);
          };
          
          ws.onmessage = (event) => {
            try {
              const message = JSON.parse(event.data);
              
              if (message.type === 'option_candle' && message.candle && candlestickSeriesRef.current) {
                // New completed candle - update or add to chart
                const candleData = {
                  time: message.candle.time, // Unix timestamp in seconds
                  open: parseFloat(message.candle.open),
                  high: parseFloat(message.candle.high),
                  low: parseFloat(message.candle.low),
                  close: parseFloat(message.candle.close)
                };
                
                // Validate candle data
                if (!candleData.time || isNaN(candleData.open) || isNaN(candleData.high) || 
                    isNaN(candleData.low) || isNaN(candleData.close)) {
                  console.warn('Invalid candle data received:', message.candle);
                  return;
                }
                
                try {
                  // Update chart with completed candle (update() handles both updating existing and adding new candles)
                  candlestickSeriesRef.current.update(candleData);
                  // Update LTP to the close price of completed candle
                  setCurrentLTP(candleData.close);
                  console.log(`📊 Live option candle completed: ${message.instrument_symbol || 'Option'} - ₹${candleData.close.toFixed(2)} at ${new Date(candleData.time * 1000).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })}`);
                  
                  // Optionally scroll to show the latest candle (only if user is near the end)
                  if (chartInstanceRef.current) {
                    const timeScale = chartInstanceRef.current.timeScale();
                    const visibleRange = timeScale.getVisibleRange();
                    if (visibleRange && visibleRange.to) {
                      // If user is viewing recent data (within last 30 minutes), auto-scroll
                      const now = Math.floor(Date.now() / 1000);
                      const candleTime = candleData.time;
                      if (now - candleTime < 1800) { // 30 minutes
                        // Scroll to show latest candle
                        setTimeout(() => {
                          timeScale.scrollToPosition(1, false); // Scroll to end
                        }, 100);
                      }
                    }
                  }
                } catch (updateErr) {
                  console.warn('Failed to update chart with completed candle:', updateErr);
                }
              } else if (message.type === 'current_candle' && message.candle && candlestickSeriesRef.current) {
                // Current forming candle - update in place (real-time price updates)
                const candleData = {
                  time: message.candle.time, // Unix timestamp in seconds
                  open: parseFloat(message.candle.open),
                  high: parseFloat(message.candle.high),
                  low: parseFloat(message.candle.low),
                  close: parseFloat(message.candle.close)
                };
                
                // Validate candle data
                if (!candleData.time || isNaN(candleData.open) || isNaN(candleData.high) || 
                    isNaN(candleData.low) || isNaN(candleData.close)) {
                  console.warn('Invalid current candle data received:', message.candle);
                  return;
                }
                
                try {
                  // Update the forming candle in real-time
                  candlestickSeriesRef.current.update(candleData);
                  // Update LTP in real-time
                  setCurrentLTP(candleData.close);
                } catch (updateErr) {
                  console.warn('Failed to update chart with current candle:', updateErr);
                }
              } else if (message.type === 'connected') {
                console.log(`✅ ${message.message || 'Connected to live option data'}`);
                setWsConnected(true);
              }
            } catch (parseErr) {
              console.error('Error parsing WebSocket message:', parseErr);
            }
          };
          
          ws.onerror = (error) => {
            console.error('Option WebSocket error:', error);
          };
          
          ws.onclose = () => {
            console.log(`🔌 Option WebSocket closed for token ${chartPopup.instrumentToken}`);
            optionWebSocketRef.current = null;
            setWsConnected(false);
          };
        } catch (wsErr) {
          console.error('Failed to create WebSocket connection:', wsErr);
        }
      }

      // Handle resize
      const handleResize = () => {
        if (chartContainerRef.current && chartInstanceRef.current) {
          chartInstanceRef.current.applyOptions({
            width: chartContainerRef.current.clientWidth
          });
        }
      };
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        // Close WebSocket when chart closes
        if (optionWebSocketRef.current) {
          optionWebSocketRef.current.close();
          optionWebSocketRef.current = null;
        }
      };
    }
  }, [chartPopup.isOpen, chartPopup.data, chartPopup.instrumentToken]);

  // Cleanup chart and WebSocket on unmount
  useEffect(() => {
    return () => {
      // Close WebSocket
      if (optionWebSocketRef.current) {
        optionWebSocketRef.current.close();
        optionWebSocketRef.current = null;
      }
      // Remove chart
      if (chartInstanceRef.current) {
        chartInstanceRef.current.remove();
        chartInstanceRef.current = null;
      }
      candlestickSeriesRef.current = null;
    };
  }, []);

  if (loading && signals.length === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        {!isMobileEmbed && <Navbar />}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading signal history...</p>
          </div>
        </div>
        {!isMobileEmbed && <Footer />}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {!isMobileEmbed && <Navbar />}

      <div className={`flex-1 container mx-auto px-4 max-w-7xl ${isMobileEmbed ? 'py-3' : 'py-8'}`}>
      {!isMobileEmbed && (
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">NIFTY Signal History</h1>
          <p className="text-gray-600">View all signals generated by the NIFTY Signal Analyzer</p>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Strategy</label>
            <select
              value={strategyType}
              onChange={(e) => { setStrategyType(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Strategies</option>
              <option value="zone_reversal">Zone Reversal</option>
              <option value="pivot_breakout">Pivot Breakout</option>
              <option value="pivot_trend_continuation">Pivot Trend Continuation</option>
              <option value="ema_pullback">EMA Pullback</option>
              <option value="daily_st">Daily SuperTrend</option>
              <option value="ath_fade">ATH Fade</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Signal Type</label>
            <select
              value={signalType}
              onChange={(e) => { setSignalType(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="Bullish">Bullish</option>
              <option value="Bearish">Bearish</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Outcome</label>
            <select
              value={outcome}
              onChange={(e) => { setOutcome(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Outcomes</option>
              <option value="Win">Win</option>
              <option value="Loss">Loss</option>
              <option value="Breakeven">Breakeven</option>
              <option value="Pending">Pending</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        
        <div className="mt-4 flex gap-3 items-center">
          <button
            onClick={handleResetFilters}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            Reset Filters
          </button>
          <button
            onClick={trackSignalOutcomes}
            disabled={trackingLoading}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${
              trackingLoading 
                ? 'bg-blue-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {trackingLoading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Tracking...
              </span>
            ) : (
              'Track Pending Outcomes'
            )}
          </button>
        </div>
        
        {/* Tracking Result Message */}
        {trackingResult && (
          <div className={`mt-3 p-3 rounded-md text-sm ${
            trackingResult.type === 'success' ? 'bg-green-50 text-green-700' :
            trackingResult.type === 'error' ? 'bg-red-50 text-red-700' :
            'bg-blue-50 text-blue-700'
          }`}>
            {trackingResult.message}
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="mb-4 text-sm text-gray-600">
        Showing {signals.length} of {totalCount} signals
      </div>

      {/* Signals Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {signals.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No signals found matching your filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Signal</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Strategy</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entry</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stop Loss</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outcome</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P&L %</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {signals.map((signal) => (
                  <React.Fragment key={signal.signal_id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        <div>{formatDate(signal.timestamp)}</div>
                        {isEmaPullbackSignal(signal) && getEmaPullbackTriggerBarLabel(signal) && (
                          <div className="mt-0.5 text-xs text-indigo-600" title="Closed 5m bar that triggered EMA Pullback">
                            Trigger bar: {getEmaPullbackTriggerBarLabel(signal)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getSignalTypeColor(signal.signal_type)}`}>
                          {signal.signal_type} {signal.option_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {signal.strategy_name}
                        {signal.confluence_score && (
                          <span className="ml-1 text-xs text-gray-500">({signal.confluence_score}/4)</span>
                        )}
                        {isEmaPullbackSignal(signal) && getEmaPullbackFastPathDetail(signal) && (
                          <div className="mt-1 max-w-xs text-xs text-gray-500 line-clamp-2" title={getEmaPullbackFastPathDetail(signal)}>
                            {getEmaPullbackFastPathDetail(signal)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatPriceWithIndex(getTradeLevels(signal).entry, getTradeLevels(signal).indexEntry)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatPriceWithIndex(getTradeLevels(signal).target1, getTradeLevels(signal).indexTarget1)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatPriceWithIndex(getTradeLevels(signal).stopLoss, getTradeLevels(signal).indexStopLoss)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {signal.outcome.status ? (
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getOutcomeColor(signal.outcome.status)}`}>
                            {signal.outcome.status}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {signal.outcome.profit_loss_pct !== null && signal.outcome.profit_loss_pct !== undefined ? (
                          <span className={signal.outcome.profit_loss_pct >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {signal.outcome.profit_loss_pct >= 0 ? '+' : ''}{signal.outcome.profit_loss_pct.toFixed(2)}%
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <div className="flex flex-col gap-1">
                          <div className="flex gap-2">
                            <button
                              onClick={() => toggleExpand(signal.signal_id)}
                              className="text-blue-600 hover:text-blue-800 font-medium"
                            >
                              {expandedSignal === signal.signal_id ? 'Hide' : 'View'} Details
                            </button>
                            {(signal.outcome.status === 'Pending' || !signal.outcome.status) && (
                              <button
                                onClick={() => trackSingleSignal(signal.signal_id)}
                                className="text-green-600 hover:text-green-800 font-medium"
                                title="Track current LTP and check if target/SL hit"
                              >
                                Track
                              </button>
                            )}
                            <button
                              onClick={() => checkLivePnL(signal)}
                              disabled={livePnLLoading[signal.signal_id]}
                              className="text-purple-600 hover:text-purple-800 font-medium disabled:opacity-50"
                              title="Check current live P&L"
                            >
                              {livePnLLoading[signal.signal_id] ? '...' : 'Live P&L'}
                            </button>
                          </div>
                          {/* Live P&L Result */}
                          {livePnL[signal.signal_id] && !livePnL[signal.signal_id].error && (
                            <div className="text-xs mt-1 p-2 bg-gray-100 rounded">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">LTP:</span>
                                <span className="text-blue-600 font-semibold">₹{livePnL[signal.signal_id].currentLTP?.toFixed(2)}</span>
                                <span className={`font-semibold ${livePnL[signal.signal_id].pnlPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  ({livePnL[signal.signal_id].pnlPercent >= 0 ? '+' : ''}{livePnL[signal.signal_id].pnlPercent?.toFixed(2)}%)
                                </span>
                              </div>
                              <div className="text-gray-500">
                                {livePnL[signal.signal_id].status} • {livePnL[signal.signal_id].timestamp}
                              </div>
                            </div>
                          )}
                          {livePnL[signal.signal_id]?.error && (
                            <div className="text-xs text-red-500 mt-1">
                              {livePnL[signal.signal_id].error}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedSignal === signal.signal_id && (
                      <tr>
                        <td colSpan="9" className="px-4 py-4 bg-gray-50">
                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {/* Market Context */}
                            <div className="min-w-0">
                              <h4 className="font-semibold text-gray-900 mb-2">Market Context</h4>
                              <div className="space-y-1 text-sm break-words">
                                <p><span className="font-medium">Spot Price:</span> ₹{signal.spot_price.toFixed(2)}</p>
                                <p><span className="font-medium">Market Bias:</span> {signal.market_bias || 'N/A'}</p>
                                <p><span className="font-medium">Market Regime:</span> {signal.market_regime || 'N/A'}</p>
                                {signal.india_vix && (
                                  <p><span className="font-medium">India VIX:</span> {signal.india_vix.toFixed(2)}</p>
                                )}
                              </div>
                            </div>
                            
                            {/* Indicators */}
                            <div className="min-w-0">
                              <h4 className="font-semibold text-gray-900 mb-2">Indicators</h4>
                              <div className="space-y-1 text-sm break-words">
                                {signal.indicators.rsi_14 && (
                                  <p><span className="font-medium">RSI:</span> {signal.indicators.rsi_14.toFixed(2)}</p>
                                )}
                                {signal.indicators.ema_9 && (
                                  <p><span className="font-medium">EMA 9:</span> ₹{signal.indicators.ema_9.toFixed(2)}</p>
                                )}
                                {signal.indicators.ema_20 && (
                                  <p><span className="font-medium">EMA 20:</span> ₹{signal.indicators.ema_20.toFixed(2)}</p>
                                )}
                                {signal.indicators.vwap && (
                                  <p><span className="font-medium">VWAP:</span> ₹{signal.indicators.vwap.toFixed(2)}</p>
                                )}
                              </div>
                            </div>
                            
                            {/* EMA Pullback setup */}
                            {isEmaPullbackSignal(signal) && (
                              <div className="min-w-0">
                                <h4 className="font-semibold text-gray-900 mb-2">EMA Pullback Setup</h4>
                                <div className="space-y-1 text-sm break-words">
                                  {getEmaPullbackTriggerBarLabel(signal) && (
                                    <p>
                                      <span className="font-medium">Trigger bar (closed 5m):</span>{' '}
                                      {getEmaPullbackTriggerBarLabel(signal)}
                                    </p>
                                  )}
                                  {getEmaPullbackFastPathDetail(signal) && (
                                    <p>
                                      <span className="font-medium">Touch / reclaim:</span>{' '}
                                      {getEmaPullbackFastPathDetail(signal)}
                                    </p>
                                  )}
                                  {(signal.ema_pullback_proximity_buffer_pts ?? signal?.conditions_met?.ema_pullback_proximity_buffer_pts) != null && (
                                    <p>
                                      <span className="font-medium">Proximity buffer:</span>{' '}
                                      {Number(signal.ema_pullback_proximity_buffer_pts ?? signal.conditions_met.ema_pullback_proximity_buffer_pts).toFixed(1)} pts
                                    </p>
                                  )}
                                  <p className="text-xs text-gray-500">
                                    Signal time is when the job ran; the trigger bar is the last closed 5m candle evaluated.
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* Trade Details */}
                            <div className="min-w-0">
                              <h4 className="font-semibold text-gray-900 mb-2">Trade Details</h4>
                              <div className="space-y-1 text-sm break-words">
                                {signal.trade_details.instrument && (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="break-all"><span className="font-medium">Instrument:</span> {signal.trade_details.instrument}</p>
                                    <button
                                      onClick={() => openOptionChart(signal.trade_details.instrument)}
                                      className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                                      title="View live price chart"
                                    >
                                      View Chart
                                    </button>
                                  </div>
                                )}
                                {signal.trade_details.strike_price && (
                                  <p><span className="font-medium">Strike:</span> ₹{signal.trade_details.strike_price.toFixed(2)}</p>
                                )}
                                {getTradeLevels(signal).target2 != null && (
                                  <p><span className="font-medium">Target 2:</span> {formatPriceWithIndex(getTradeLevels(signal).target2, getTradeLevels(signal).indexTarget2)}</p>
                                )}
                                {getTradeLevels(signal).target3 != null && (
                                  <p><span className="font-medium">Target 3:</span> {formatPriceWithIndex(getTradeLevels(signal).target3, getTradeLevels(signal).indexTarget3)}</p>
                                )}
                              </div>
                            </div>
                            
                            {/* Outcome Details - Always show entry details */}
                            <div className="min-w-0">
                              <h4 className="font-semibold text-gray-900 mb-2">Outcome Details</h4>
                              <div className="space-y-1 text-sm break-words">
                                {/* Entry Details */}
                                <div className="pb-2 border-b border-gray-200 mb-2">
                                  <p className="text-green-700 font-medium mb-1">Entry (Buy)</p>
                                  <p><span className="font-medium">Entry Price:</span> {formatPriceWithIndex(getTradeLevels(signal).entry, getTradeLevels(signal).indexEntry)}</p>
                                  <p><span className="font-medium">Entry Time:</span> {formatDate(signal.timestamp)}</p>
                                  {isEmaPullbackSignal(signal) && getEmaPullbackTriggerBarLabel(signal) && (
                                    <p>
                                      <span className="font-medium">Trigger Bar:</span>{' '}
                                      {getEmaPullbackTriggerBarLabel(signal)}
                                    </p>
                                  )}
                                  <p><span className="font-medium">Target Price:</span> {formatPriceWithIndex(getTradeLevels(signal).target1, getTradeLevels(signal).indexTarget1)}</p>
                                  <p><span className="font-medium">Stop Loss:</span> {formatPriceWithIndex(getTradeLevels(signal).stopLoss, getTradeLevels(signal).indexStopLoss)}</p>
                                </div>
                                
                                {/* Exit Details - Only show if resolved */}
                                {signal.outcome.status && signal.outcome.status !== 'Pending' && (
                                  <div className="pt-1">
                                    <p className={`font-medium mb-1 ${signal.outcome.status === 'Win' ? 'text-green-700' : signal.outcome.status === 'Loss' ? 'text-red-700' : 'text-gray-700'}`}>
                                      Exit ({signal.outcome.status})
                                    </p>
                                    {signal.outcome.exit_price && (
                                      <p><span className="font-medium">Exit Price:</span> ₹{signal.outcome.exit_price.toFixed(2)}</p>
                                    )}
                                    {signal.outcome.exit_timestamp && (
                                      <p><span className="font-medium">Exit Time:</span> {formatDate(signal.outcome.exit_timestamp)}</p>
                                    )}
                                    {signal.outcome.holding_minutes !== null && signal.outcome.holding_minutes !== undefined && (
                                      <p><span className="font-medium">Holding Period:</span> {signal.outcome.holding_minutes >= 60 ? `${Math.floor(signal.outcome.holding_minutes / 60)}h ${signal.outcome.holding_minutes % 60}m` : `${signal.outcome.holding_minutes}m`}</p>
                                    )}
                                    {signal.outcome.exit_reason && (
                                      <p><span className="font-medium">Exit Reason:</span> {signal.outcome.exit_reason}</p>
                                    )}
                                    {signal.outcome.profit_loss_pct !== null && signal.outcome.profit_loss_pct !== undefined && (
                                      <p>
                                        <span className="font-medium">P&L:</span>{' '}
                                        <span className={signal.outcome.profit_loss_pct >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                                          {signal.outcome.profit_loss_pct >= 0 ? '+' : ''}{signal.outcome.profit_loss_pct.toFixed(2)}%
                                          {signal.outcome.profit_loss_amount && ` (₹${signal.outcome.profit_loss_amount >= 0 ? '+' : ''}${signal.outcome.profit_loss_amount.toFixed(2)})`}
                                        </span>
                                      </p>
                                    )}
                                    {signal.outcome.target_hit && (
                                      <p><span className="font-medium">Target Hit:</span> T{signal.outcome.target_hit}</p>
                                    )}
                                  </div>
                                )}
                                
                                {/* Show tracking info if still pending */}
                                {(!signal.outcome.status || signal.outcome.status === 'Pending') && (
                                  <div className="pt-1">
                                    <p className="text-blue-700 font-medium mb-1">Status: Tracking</p>
                                    <p className="text-gray-500 text-xs">Monitoring for 15 minutes after entry</p>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* ML Predictions */}
                            {signal.ml_predictions.confidence_score && (
                              <div className="min-w-0">
                                <h4 className="font-semibold text-gray-900 mb-2">ML Predictions</h4>
                                <div className="space-y-1 text-sm break-words">
                                  <p><span className="font-medium">Confidence:</span> {(signal.ml_predictions.confidence_score * 100).toFixed(1)}%</p>
                                  {signal.ml_predictions.success_probability && (
                                    <p><span className="font-medium">Success Probability:</span> {(signal.ml_predictions.success_probability * 100).toFixed(1)}%</p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Page {page} of {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
      </div>
      
      {/* Option Chart Popup Modal */}
      {chartPopup.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={closeChartPopup}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              {/* Header */}
              <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-between items-center border-b">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      {chartPopup.instrument} - 5 Minute Chart
                    </h3>
                    {wsConnected && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                        Live
                      </span>
                    )}
                  </div>
                  {chartPopup.data && (
                    <p className="mt-1 text-sm text-gray-500">
                      Strike: ₹{chartPopup.data.strike} | 
                      Current LTP: <span className={`font-semibold ${wsConnected ? 'text-green-600 animate-pulse' : 'text-blue-600'}`}>
                        ₹{(currentLTP ?? chartPopup.data.current_ltp)?.toFixed(2) || 'N/A'}
                      </span> |
                      Type: {chartPopup.data.option_type}
                      {wsConnected && (
                        <span className="ml-2 text-xs text-green-600">● Live</span>
                      )}
                    </p>
                  )}
                </div>
                <button
                  onClick={closeChartPopup}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Chart Content */}
              <div className="px-4 py-4 sm:px-6">
                {chartPopup.loading && (
                  <div className="flex items-center justify-center h-96">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-4 text-gray-600">Loading chart data...</p>
                    </div>
                  </div>
                )}

                {chartPopup.error && (
                  <div className="flex items-center justify-center h-96">
                    <div className="text-center text-red-600">
                      <svg className="h-12 w-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <p>{chartPopup.error}</p>
                    </div>
                  </div>
                )}

                {!chartPopup.loading && !chartPopup.error && chartPopup.data && (
                  <div ref={chartContainerRef} className="w-full h-96" />
                )}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end border-t">
                <button
                  type="button"
                  onClick={closeChartPopup}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!isMobileEmbed && <Footer />}
    </div>
  );
};

export default NiftySignalHistoryPage;

